import saltConfig from "../../config/slot-salt.json";

const DEFAULT_SALT = (saltConfig && saltConfig.salt) || "pl-2026-w44-9f3a1c8e";
const PREVIOUS_SALT = (saltConfig && saltConfig.previousSalt) || "";
const DEFAULT_PREFIX = (saltConfig && saltConfig.prefix) || "x7k9m2p4";

function deriveKeyBytes(seed, length) {
    let h1 = 0x811c9dc5;
    let h2 = 0x01000193;

    for (let i = 0; i < seed.length; i++) {
        const c = seed.charCodeAt(i) & 0xff;
        h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
        h2 = Math.imul(h2 ^ ((c + i) & 0xff), 0x85ebca6b) >>> 0;
    }

    const out = new Uint8Array(length);
    for (let i = 0; i < length; i++) {
        h1 = Math.imul(h1 ^ (i & 0xff), 0x85ebca6b) >>> 0;
        h2 = Math.imul(h2 ^ ((h1 >>> 13) & 0x7ffff), 0xc2b2ae35) >>> 0;
        out[i] = ((h1 ^ h2) >>> ((i % 4) * 8)) & 0xff;
    }

    return out;
}

function toBase64Url(bytes) {
    let binary = "";
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }

    return btoa(binary)
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/g, "");
}

function fromBase64Url(token) {
    const normalized = token.replace(/-/g, "+").replace(/_/g, "/");
    const pad = normalized.length % 4 === 0 ? "" : "=".repeat(4 - (normalized.length % 4));
    const binary = atob(normalized + pad);
    const out = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i++) {
        out[i] = binary.charCodeAt(i);
    }

    return out;
}

function randomNonce() {
    const nonce = new Uint8Array(4);
    if (typeof window !== "undefined" && window.crypto && window.crypto.getRandomValues) {
        window.crypto.getRandomValues(nonce);
    } else {
        for (let i = 0; i < 4; i++) {
            nonce[i] = Math.floor(Math.random() * 256);
        }
    }

    return nonce;
}

function bytesToHex(bytes) {
    let hex = "";
    for (let i = 0; i < bytes.length; i++) {
        hex += bytes[i].toString(16).padStart(2, "0");
    }

    return hex;
}

export function encodePacket(payload, salt = DEFAULT_SALT) {
    const json = JSON.stringify(payload);
    const raw = new TextEncoder().encode(json);
    const nonce = randomNonce();
    const key = deriveKeyBytes(`${salt}:${bytesToHex(nonce)}`, raw.length);
    const out = new Uint8Array(4 + raw.length);

    out.set(nonce, 0);
    for (let i = 0; i < raw.length; i++) {
        out[4 + i] = raw[i] ^ key[i];
    }

    return toBase64Url(out);
}

export function decodePacket(token, salt = DEFAULT_SALT) {
    if (!token || typeof token !== "string") {
        return null;
    }

    try {
        const bytes = fromBase64Url(token);
        if (bytes.length <= 4) {
            return null;
        }

        const nonce = bytes.subarray(0, 4);
        const cipher = bytes.subarray(4);
        const nonceHex = bytesToHex(nonce);
        const salts = salt === DEFAULT_SALT && PREVIOUS_SALT
            ? [DEFAULT_SALT, PREVIOUS_SALT]
            : [salt];

        for (let s = 0; s < salts.length; s++) {
            try {
                const key = deriveKeyBytes(`${salts[s]}:${nonceHex}`, cipher.length);
                const plain = new Uint8Array(cipher.length);

                for (let i = 0; i < cipher.length; i++) {
                    plain[i] = cipher[i] ^ key[i];
                }

                return JSON.parse(new TextDecoder().decode(plain));
            } catch (innerErr) {
                // try next salt
            }
        }

        return null;
    } catch (e) {
        return null;
    }
}

export function getPreviewMode() {
    if (typeof window === "undefined" || !window.location || !window.location.search) {
        return "";
    }

    try {
        const params = new URLSearchParams(window.location.search);

        return params.get("__pl_pv") || "";
    } catch (e) {
        return "";
    }
}

export function buildSlotUrl(payload) {
    const data = Object.assign({}, payload);
    if (getPreviewMode() !== "" && !("pv" in data)) {
        data.pv = 1;
    }

    return `/_pl/${DEFAULT_PREFIX}/${encodePacket(data)}`;
}

export function sendSlotBeacon(payload) {
    const url = buildSlotUrl(payload);
    fetch(url, {
        method: "POST",
        credentials: "same-origin",
        keepalive: true
    }).catch(() => 0);
}

export function isTrustedActionEvent(event) {
    if (!event || event.isTrusted !== true) {
        return false;
    }
    if (event.type === "click") {
        return true;
    }
    if (event.type === "keydown" && (event.key === "Enter" || event.key === " ")) {
        return true;
    }

    return false;
}

export function bindTrustedActivate(element, handler, useCapture = false) {
    if (!element) {
        return;
    }
    element.addEventListener("click", handler, useCapture);
    element.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") {
            handler(event);
        }
    }, useCapture);
}

export function openTrustedUrl(event, url) {
    if (!isTrustedActionEvent(event) || !url) {
        return false;
    }

    event.preventDefault();
    const wnd = window.open(url, "_blank", "noopener,noreferrer");
    if (wnd) {
        wnd.opener = null;
    }

    return true;
}
